import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialCallout, EditorialSubsection } from '../../report/editorial';
import { buildWorkloadPowerFitContent } from '../../../../lib/report/workloadPowerFitContent';
import { projectDerivedTableReferences } from '../../../../lib/report/derivedTableReferences';
import { walkCitationText } from '../../../../lib/walkCitationText';

/**
 * WorkloadPowerFitSection — cited, catalog-driven section shared by the
 * Feasibility and Power reports. Reads the site's effective workload profile and
 * renders the rack-density / cooling / PUE / power-profile datapoints plus the
 * behind-the-meter power-source fit, all sourced. Pure function of
 * `buildWorkloadPowerFitContent` (the same builder the DOCX + PPTX exporters use),
 * so all four surfaces stay in lock-step. Always has data (profile falls back to
 * 'mixed'), so it never renders an empty shell.
 */
export const WorkloadPowerFitSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const projectedReport = projectDerivedTableReferences(report, { forceWorkload: true }).report;
  const c = buildWorkloadPowerFitContent(projectedReport);
  const hair = roleColor(theme, 'hair');
  const cited = (text: string): React.ReactNode => (
    walkCitationText(text, projectedReport.references)
  );

  return (
    <SectionShell label={label} hasData={true}>
      <div className="space-y-4">
        <p className="text-sm" style={{ color: roleColor(theme, 'body') }}>{c.intro}</p>

        {/* EN-dash, not U+2014: house style forbids em-dashes on a rendered report surface. */}
        <EditorialSubsection theme={theme} label={`Workload profile – ${c.profileLabel}`}>
          <dl className="text-sm space-y-1" style={{ color: roleColor(theme, 'body') }}>
            {c.techRows.map((r) => (
              <div key={r.label} className="flex gap-3">
                <dt className="font-semibold shrink-0" style={{ minWidth: '10rem' }}>{r.label}</dt>
                <dd>{cited(r.value)}</dd>
              </div>
            ))}
          </dl>
        </EditorialSubsection>

        <EditorialCallout theme={theme} label="BTM power fit">
          {cited(c.btmFitLine)}
        </EditorialCallout>

        <EditorialSubsection theme={theme} label="BTM power source options">
          {/* `data-span-columns` on the table's block-formatting-context wrapper is the N2
              two-column enabler: a wide table inside a 2-col page flow must span BOTH columns
              or it is crushed into one. The visual-matrix span-hook guard fails any `table`
              whose nearest BFC lacks this ancestor. */}
          <div
            data-span-columns
            data-testid="btm-source-table-flow"
            className="min-w-0"
          >
            <table
              className="w-full text-xs border-collapse"
              data-table-splittable="true"
              style={{ breakInside: 'auto', pageBreakInside: 'auto', tableLayout: 'fixed' }}
            >
              <thead>
                <tr>
                  {c.btmTable.headers.map((h) => (
                    <th
                      key={h}
                      className="text-left font-semibold px-2 py-1"
                      style={{ color: roleColor(theme, 'muted'), borderBottom: `1px solid ${hair}` }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {c.btmTable.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td
                        key={j}
                        className="px-2 py-1 align-top"
                        style={{ color: roleColor(theme, 'body'), borderBottom: `1px solid ${hair}` }}
                      >
                        {cited(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </EditorialSubsection>

      </div>
    </SectionShell>
  );
};
